import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import api, { userAPI } from '../../services/api';
import { 
  Settings, Bell, Eye, EyeOff, Lock, Shield, 
  Save, Loader2, CheckCircle, AlertCircle, Mail, MessageSquare
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';

const SettingsPage = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  
  // Settings state
  const [settings, setSettings] = useState({
    discreet_mode: false,
    notification_email: true,
    notification_sms: false,
  });
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await userAPI.getProfile();
        setSettings({
          discreet_mode: response.data.discreet_mode || false,
          notification_email: response.data.notification_email !== false,
          notification_sms: response.data.notification_sms || false,
        });
      } catch (err) {
        console.error('Failed to fetch settings:', err);
      }
    };
    
    fetchSettings();
  }, []);

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const saveSettings = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await userAPI.updateProfile(settings);
      setSuccess('Einstellungen gespeichert');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('Passwörter stimmen nicht überein');
      return;
    }
    
    if (passwordData.new_password.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    setChangingPassword(true);
    setError('');
    setSuccess('');

    try {
      await api.post('/users/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });
      setSuccess('Passwort erfolgreich geändert');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Ändern des Passworts');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-2xl font-bold text-white">{t('nav.settings')}</h1>
          <p className="text-slate-400 mt-1">Verwalten Sie Ihre Kontoeinstellungen und Benachrichtigungen</p>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-500" />
            <p className="text-emerald-400">{success}</p>
          </div>
        )}
        
        {error && (
          <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Appearance Settings */}
        <div className="glass-card p-6">
          <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Eye className="w-5 h-5 text-[#EC2229]" />
            Darstellung
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
              <div className="flex items-center gap-4">
                {settings.discreet_mode ? (
                  <EyeOff className="w-5 h-5 text-slate-400" />
                ) : (
                  <Eye className="w-5 h-5 text-slate-400" />
                )}
                <div>
                  <p className="text-white font-medium">Diskreter Modus</p>
                  <p className="text-sm text-slate-400">
                    Versteckt Guthaben und Preise vor neugierigen Blicken
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.discreet_mode}
                onCheckedChange={(checked) => handleSettingChange('discreet_mode', checked)}
                data-testid="discreet-mode-switch"
              />
            </div>
          </div>
        </div>

        {/* Notification Settings */}
        <div className="glass-card p-6">
          <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Bell className="w-5 h-5 text-[#EC2229]" />
            Benachrichtigungen
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
              <div className="flex items-center gap-4">
                <Mail className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-white font-medium">E-Mail Benachrichtigungen</p>
                  <p className="text-sm text-slate-400">
                    Erhalten Sie Updates zu Ihren Dateien per E-Mail
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.notification_email}
                onCheckedChange={(checked) => handleSettingChange('notification_email', checked)}
                data-testid="email-notification-switch"
              />
            </div>
            
            <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
              <div className="flex items-center gap-4">
                <MessageSquare className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-white font-medium">SMS Benachrichtigungen</p>
                  <p className="text-sm text-slate-400">
                    Erhalten Sie SMS wenn Ihre Datei fertig ist
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.notification_sms}
                onCheckedChange={(checked) => handleSettingChange('notification_sms', checked)}
                data-testid="sms-notification-switch"
              />
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <Button 
              onClick={saveSettings}
              disabled={saving}
              className="btn-primary"
              data-testid="save-settings"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Speichern...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Einstellungen speichern
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Security Settings */}
        <div className="glass-card p-6">
          <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Lock className="w-5 h-5 text-[#EC2229]" />
            Sicherheit
          </h3>
          
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div className="space-y-2">
              <Label className="label-text">Aktuelles Passwort</Label>
              <Input
                type="password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))}
                placeholder="••••••••"
                className="input-field"
                required
                data-testid="current-password"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-text">Neues Passwort</Label>
                <Input
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                  placeholder="••••••••"
                  className="input-field"
                  required
                  data-testid="new-password"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="label-text">Passwort bestätigen</Label>
                <Input
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                  placeholder="••••••••"
                  className="input-field"
                  required
                  data-testid="confirm-password"
                />
              </div>
            </div>
            
            <div className="flex justify-end">
              <Button 
                type="submit"
                disabled={changingPassword}
                className="btn-secondary"
                data-testid="change-password-btn"
              >
                {changingPassword ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Ändern...
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4 mr-2" />
                    Passwort ändern
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>

        {/* Account Info */}
        <div className="glass-card p-6">
          <h3 className="font-heading text-lg font-semibold text-white mb-4">
            Kontoinformationen
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="p-3 bg-white/5 rounded-lg">
              <p className="text-slate-500">Konto-ID</p>
              <p className="text-white font-mono text-xs truncate">{user?.id}</p>
            </div>
            <div className="p-3 bg-white/5 rounded-lg">
              <p className="text-slate-500">Rolle</p>
              <p className="text-white">{user?.role}</p>
            </div>
            <div className="p-3 bg-white/5 rounded-lg">
              <p className="text-slate-500">Erstellt am</p>
              <p className="text-white">{new Date(user?.created_at).toLocaleDateString()}</p>
            </div>
            <div className="p-3 bg-white/5 rounded-lg">
              <p className="text-slate-500">Status</p>
              <span className="badge-success">Aktiv</span>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SettingsPage;
